import axios from 'axios';
import { resolveAuthHeaderValue } from '@/utils/authToken';
import { wpbootUrl } from '@/utils/appConfig';

const secondApiClient = axios.create({
  baseURL: wpbootUrl(),
});

secondApiClient.interceptors.request.use(config => {
  const authHeaderValue = resolveAuthHeaderValue();
  if (!authHeaderValue) return config;
  const nextConfig = { ...config };
  if (!nextConfig.headers) nextConfig.headers = {};
  nextConfig.headers.Authorization = authHeaderValue;
  return nextConfig;
});

const trimTrailingSlash = value => String(value || '').replace(/\/+$/, '');

export const RUNNING_STATUSES = ['CREATED', 'RUNNING', 'STARTED'];
export const TERMINAL_STATUSES = ['SUCCESS', 'FAILED'];

const toPositiveInt = (value, fallback, { min = 1, max = Number.POSITIVE_INFINITY } = {}) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return fallback;
  return Math.min(max, Math.max(min, Math.round(num)));
};

const toNonNegativeInt = (value, fallback = 0) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return fallback;
  return Math.max(0, Math.round(num));
};

export async function createDeployment(formData) {
  const { data } = await secondApiClient.post('/api/wp-deployments', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function getDeploymentStatus(deploymentId) {
  const { data } = await secondApiClient.get(`/api/wp-deployments/${encodeURIComponent(deploymentId)}`);
  return data;
}

export async function getDeploymentLogs(deploymentId) {
  const { data } = await secondApiClient.get(`/api/wp-deployments/${encodeURIComponent(deploymentId)}/logs`);
  return data;
}

export async function getDeploymentsHistory(options = {}) {
  const page = toNonNegativeInt(options?.page, 0);
  const size = toPositiveInt(options?.size, 20, { min: 1, max: 100 });
  const { data } = await secondApiClient.get('/api/wp-deployments', { params: { page, size } });

  if (Array.isArray(data)) {
    return {
      items: data,
      page,
      size,
      totalItems: data.length,
      totalPages: data.length ? 1 : 0,
      first: true,
      last: true,
    };
  }

  const items = Array.isArray(data?.items)
    ? data.items
    : (Array.isArray(data?.content) ? data.content : []);

  const rawPage = typeof data?.page === 'number' ? data.page : data?.number;
  const pageValue = toNonNegativeInt(rawPage, page);
  const sizeValue = toPositiveInt(data?.size, size, { min: 1, max: 100 });
  const totalItems = toNonNegativeInt(data?.totalItems ?? data?.totalElements, items.length);
  const totalPagesByItems = totalItems > 0 ? Math.ceil(totalItems / Math.max(1, sizeValue)) : 0;
  const totalPages = toNonNegativeInt(data?.totalPages, totalPagesByItems);
  const first = typeof data?.first === 'boolean' ? data.first : pageValue <= 0;
  const last = typeof data?.last === 'boolean' ? data.last : (totalPages <= 0 || pageValue >= totalPages - 1);

  return { items, page: pageValue, size: sizeValue, totalItems, totalPages, first, last };
}

export async function deleteDeployment(deploymentId) {
  await secondApiClient.delete(`/api/wp-deployments/${encodeURIComponent(deploymentId)}`);
}

export async function getDeploymentApplicationPassword(deploymentId) {
  const { data } = await secondApiClient.get(`/api/wp-deployments/${encodeURIComponent(deploymentId)}/application-password`);
  return data;
}

export function buildDeploymentEventsUrl(deploymentId) {
  const path = `/api/wp-deployments/${encodeURIComponent(deploymentId)}/events`;
  const base = trimTrailingSlash(wpbootUrl());
  if (!base) return path;
  return `${base}${path}`;
}

export function isTerminalStatus(status) {
  return TERMINAL_STATUSES.includes(String(status || '').toUpperCase());
}

export function isRunningStatus(status) {
  return RUNNING_STATUSES.includes(String(status || '').toUpperCase());
}
